import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../../contexts/AuthContext";
import { fmtAgo } from "../../lib/format";
import { cn } from "../../lib/utils";
import {
  fetchUnmatchedTags,
  type UnmatchedTagBox,
  type UnmatchedTagsResponse,
} from "../../services/alertsApi";

/** Scan-freshness options. A box whose newest scan is months old is stale
 *  inventory rather than a live alert, so the default is the tight window. */
const WINDOWS: { label: string; days: number | null }[] = [
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
  { label: "All time", days: null },
];

/** Copy text to the clipboard, resolving false when the browser refuses
 *  (insecure origin, permissions) so the caller can stay quiet about it. */
async function copy(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function CopyButton({
  text,
  label,
  className,
}: {
  text: string;
  label: string;
  className?: string;
}): React.ReactElement {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void copy(text).then((ok) => {
          if (!ok) return;
          setDone(true);
          window.setTimeout(() => setDone(false), 1200);
        });
      }}
      className={cn(
        "px-2 py-0.5 text-xs rounded border border-secondary-300 bg-white",
        "text-secondary-700 hover:bg-secondary-50",
        className,
      )}
    >
      {done ? "Copied" : label}
    </button>
  );
}

function BoxCard({ box }: { box: UnmatchedTagBox }): React.ReactElement {
  const [open, setOpen] = useState(false);
  const label = box.smartbox_code || box.scu_name;
  const pctOfScan =
    box.total_tags && box.total_tags > 0
      ? Math.round((box.unmatched_count / box.total_tags) * 100)
      : null;

  return (
    <div className="mb-4 rounded-md border border-secondary-200">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-3 py-2 bg-secondary-50 rounded-t-md">
        <Link
          to={`/inventory/${encodeURIComponent(box.scu_name)}`}
          className="text-sm font-semibold text-primary-700 hover:underline"
        >
          {label}
        </Link>
        <span className="text-sm text-secondary-700">
          <span className="font-semibold tabular-nums">{box.unmatched_count}</span>
          {box.total_tags != null && (
            <> of {box.total_tags} tags unmatched</>
          )}
          {pctOfScan != null && (
            <span className="text-secondary-500"> ({pctOfScan}%)</span>
          )}
        </span>
        <span className="text-xs text-secondary-500">
          {box.scan_end_utc ? `scanned ${fmtAgo(box.scan_end_utc)} ago` : "never scanned"}
        </span>
        <span className="ml-auto flex items-center gap-2">
          <CopyButton
            text={box.epcs.map((e) => e.epc).join("\n")}
            label={`Copy ${box.epcs.length} EPC${box.epcs.length === 1 ? "" : "s"}`}
          />
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="px-2 py-0.5 text-xs rounded border border-secondary-300 bg-white text-secondary-700 hover:bg-secondary-50"
          >
            {open ? "Hide EPCs" : "Show EPCs"}
          </button>
        </span>
      </div>

      {open && (
        <div className="px-3 py-2">
          {box.epcs_truncated && (
            <p className="mb-2 text-xs text-amber-700">
              Showing the first {box.epcs.length} of {box.unmatched_count} — the
              rest are in this box's inventory page.
            </p>
          )}
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4">
            {box.epcs.map((e) => (
              <li
                key={e.epc}
                className="flex items-center gap-2 py-0.5 font-mono text-xs text-secondary-800"
              >
                <span className="truncate" title={e.epc}>
                  {e.epc}
                </span>
                {e.reason === "item_missing" && (
                  <span
                    className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-sans"
                    title="This tag IS matched, but the product it points at was deleted — repoint or restore the product rather than re-matching the tag."
                  >
                    product deleted
                  </span>
                )}
                <CopyButton text={e.epc} label="Copy" className="ml-auto shrink-0" />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Unmatched Tags — EPCs the readers see in each box's most recent scan that
 * resolve to no product. They inflate a scan's tag total while counting toward
 * no SKU, so on-hand quietly under-reports until someone matches them.
 *
 * The window filters on SCAN FRESHNESS (not on the tag), which is why the
 * default is 7 days: a box that last scanned in May is stale inventory, not a
 * live alert.
 */
export const UnmatchedTagsTab: React.FC = () => {
  const { token } = useAuth();
  const [days, setDays] = useState<number | null>(7);
  const [data, setData] = useState<UnmatchedTagsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    fetchUnmatchedTags(token, days)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [token, days]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex rounded-md border border-secondary-300 overflow-hidden">
          {WINDOWS.map((w) => (
            <button
              key={w.label}
              type="button"
              onClick={() => setDays(w.days)}
              className={cn(
                "px-3 py-1.5 text-sm",
                days === w.days
                  ? "bg-primary-500 text-white"
                  : "bg-white text-secondary-700 hover:bg-secondary-50",
              )}
            >
              {w.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="px-3 py-1.5 text-sm rounded-md border border-secondary-300 bg-white text-secondary-700 hover:bg-secondary-50 disabled:opacity-50"
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
        {data && (
          <span className="text-sm text-secondary-600">
            {data.total_unmatched} unmatched tag
            {data.total_unmatched === 1 ? "" : "s"} across {data.box_count} box
            {data.box_count === 1 ? "" : "es"}
          </span>
        )}
      </div>

      <p className="text-xs text-secondary-500 mb-4">
        RFID tags read in a box's most recent scan that aren't associated with
        any product. They count toward the scan's tag total but toward no SKU,
        so inventory under-reports until they're matched.{" "}
        <Link to="/tags/match" className="text-primary-700 hover:underline">
          Match Tags
        </Link>{" "}
        assigns them — pick the product, then paste or scan the EPCs. The
        window filters on when the BOX last scanned, so a box that stopped
        scanning months ago only appears under “All time”.
      </p>

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          Couldn’t load unmatched tags: {error}
        </div>
      )}

      {loading && !data && (
        <div className="text-sm text-secondary-500">Loading…</div>
      )}

      {data && data.box_count === 0 && !loading && (
        <div className="rounded-md border border-secondary-200 bg-secondary-50 px-4 py-6 text-center text-sm text-secondary-600">
          Every tag scanned in this window is matched to a product. 🎉
        </div>
      )}

      {data?.boxes.map((b) => (
        <BoxCard key={b.scu_name} box={b} />
      ))}
    </div>
  );
};

export default UnmatchedTagsTab;
